import * as XLSX from "xlsx";
import { Invoice } from "../models";
import type { TranslateFn } from "../types";

/** Format an ISO date string as dd.mm.yyyy for Excel export, or "" if invalid. */
export const formatDateForExport = (dateString: string | undefined): string => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch {
    return "";
  }
};

/** Strip characters that could cause filename injection and cap the length. */
export const sanitizeFilename = (filename: string): string => {
  return filename.replace(/[^a-zA-Z0-9_\-æøåÆØÅ.]/g, "_").substring(0, 200);
};

/** Map invoices into the localized row objects used for the Excel sheet. */
export const buildInvoiceExcelData = (
  invoices: Invoice[],
  t: TranslateFn,
) =>
  invoices.map((invoice) => {
    const customerName = invoice.invoicee?.isCompany
      ? invoice.invoicee?.name
      : `${invoice.invoicee?.firstName || ""} ${invoice.invoicee?.lastName || ""}`.trim();

    return {
      [t("invoice:details.invoice_number")]: invoice.invoiceNumber || "",
      [t("common:customer")]: customerName || "",
      [t("common:project_number")]: invoice.yourReference || "",
      [t("invoice:details.invoice_date")]: formatDateForExport(
        invoice.invoiceDate,
      ),
      [t("invoice:excel_total_excl_vat")]:
        invoice.amounts?.totalAmountWithoutVat || 0,
      [t("invoice:excel_vat_amount")]: invoice.amounts?.totalVatAmount || 0,
      [t("invoice:excel_total_incl_vat")]: invoice.amounts?.totalAmount || 0,
      [t("invoice:details.invoicer")]: invoice.invoicer?.name || "",
    };
  });

/** Build the XLSX workbook + safe filename for the given invoice rows. */
export const buildInvoiceWorkbook = (
  excelData: ReturnType<typeof buildInvoiceExcelData>,
  projectNumberSearch: string,
  t: TranslateFn,
): { workbook: XLSX.WorkBook; filename: string } => {
  const ws = XLSX.utils.json_to_sheet(excelData);

  const columnWidths = [
    { wch: 15 }, // Invoice Number
    { wch: 25 }, // Customer
    { wch: 20 }, // Project Number
    { wch: 12 }, // Invoice Date
    { wch: 15 }, // Total excl VAT
    { wch: 15 }, // VAT Amount
    { wch: 15 }, // Total incl VAT
    { wch: 25 }, // Invoicer
  ];
  ws["!cols"] = columnWidths;

  const workbook = XLSX.utils.book_new();
  const sheetName = projectNumberSearch
    ? `${t("invoice:project_label")} ${projectNumberSearch}`.substring(0, 31)
    : t("invoice:invoices_label");
  XLSX.utils.book_append_sheet(workbook, ws, sheetName);

  const timestamp = new Date().toISOString().split("T")[0];
  const baseFilename = projectNumberSearch
    ? `fakturaer_${projectNumberSearch}_${timestamp}`
    : `fakturaer_${timestamp}`;
  const filename = sanitizeFilename(baseFilename) + ".xlsx";

  return { workbook, filename };
};

/** Trigger the browser download of the given workbook. */
export const writeInvoiceWorkbook = (
  workbook: XLSX.WorkBook,
  filename: string,
): void => {
  XLSX.writeFile(workbook, filename);
};
